import { addToCart } from '@entities/cart';
import { useProduct as useProductModel } from '@entities/products';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthModal } from '@features/common/protected-auth-route/model.ts';
import { useUser } from '@shared/hooks';
import { shopService } from 'library/api/shopService';
import { useAppDispatch } from 'library/hooks/common';
import type { DetailAttribute, DetailStorageItem, SelectableOption } from 'library/models/shop';
import { openNotifyModal } from 'library/redux/modal';
import type { Info, Media, Price } from 'library/types/shop';

const useProduct = () => {
  const dispatch = useAppDispatch();
  const { openAuth } = useAuthModal();
  const { search } = useLocation();
  const { id = '' } = useParams<{ id: string }>();
  const { isAuth } = useUser();
  const navigate = useNavigate();

  const productId = Number(id);
  const { addProduct } = useProductModel(productId);

  const [status, setStatus] = useState<string>('idle');
  const [initialComplete, setInitialComplete] = useState<boolean>(false);

  const [attributes, setAttributes] = useState<DetailAttribute[]>([]);
  const [storage, setStorage] = useState<DetailStorageItem[]>([]);
  const [media, setMedia] = useState<Media>({} as Media);
  const [price, setPrice] = useState<Price>({} as Price);
  const [info, setInfo] = useState<Info>({} as Info);

  const [quantity, setQuantity] = useState<string>('1');
  const [selectedQuantity, setSelectedQuantity] = useState<number>(0);
  const [attributesSelected, setAttributesSelected] = useState<boolean>(false);
  const [filterdStorage, setFilterdStorage] = useState([] as DetailStorageItem[]);
  const [showSelectNote, setShowSelectNote] = useState<boolean>(false);

  const [tables, setTables] = useState<any>([]);

  const sendToCart = async () => {
    // отправляем товар в корзину
    if (isAuth) {
      const item = { ...filterdStorage[0] };
      const quantityNumber: number = parseInt(quantity);
      if (quantity === '' || quantity === '0') {
        dispatch(
          openNotifyModal({
            title: 'Ошибка',
            text: 'Выбрано несоответствующее количество товара',
            confirmText: 'Ок',
          })
        );
        setQuantity('1');
        return;
      }
      const data = {
        count: quantityNumber > item.count ? item.count : quantityNumber,
        storageitem: item.id,
      };
      dispatch(addToCart(data));

      // сбрасываем количество товара после отправки
      setQuantity('1');
    } else {
      openAuth();
    }
  };

  useEffect(() => {
    /** Основной атрибут (у которого required: true), всегда должен быть выбран
		 	проверяем, какие параметры пришли из строки браузера */
    if (status === 'loaded' && attributes.length > 0 && !initialComplete) {
      let params = qs.parse(search);
      const paramArray = Object.keys(params);

      if (paramArray.length === 0) {
        // пользователь не указал в ссылке ни одного атрибута - выбираем один главный
        for (const attr of attributes) {
          const options = [...attr.storage_variations] as SelectableOption[];
          if (
            attr.required &&
            (attr.type === 'color' || attr.type === 'size' || attr.type === 'option')
          ) {
            const id = attr.id;
            params = { [id]: options[0]?.id.toString() };
            break;
          }
        }
        navigate({ search: qs.stringify(params) });
      }

      setInitialComplete(true);
    }
  }, [search, status, initialComplete, navigate, attributes]);

  useEffect(() => {
    // подсчитываем количество товара, отфильтрованного по характеристикам
    if (storage.length === 0 && !initialComplete && status !== 'loaded') {
      return;
    }

    // отключаем подсказку, если включена
    setShowSelectNote(false);

    const params = qs.parse(search);
    // проверяем, все ли переключаемые атрибуты выбраны и если да, включаем кнопку "отправить в корзину"
    const isAllAtributesSelected = (storage: DetailStorageItem[]) => {
      if (!storage[0]) {
        return false;
      }

      setAttributesSelected(
        Object.keys(params).length ===
          storage[0].attributevalue.filter(
            (attr) =>
              attr.attribute.type === 'color' ||
              attr.attribute.type === 'size' ||
              attr.attribute.type === 'option'
          ).length
      );
    };

    const calculateQuantity = (storage: DetailStorageItem[]) => {
      /** Подсчитываем количество товара. TODO Уточнить, может ли в storage лежать 
				несколько пачек товара с одинаковыми характеристиками
				Пока что все указывает  на то, что не может
 			*/
      let quantity = 0;
      for (let i = 0; i < storage.length; i++) {
        quantity += storage[i].count;
      }
      setSelectedQuantity(quantity);
    };

    const filterProducts = (params: any) => {
      // фильтруем товары по выбранным характеристикам и сохраняем отдельно
      let storageItems = [...storage];
      for (const param in params) {
        storageItems = storageItems.filter((item) => {
          return (
            item.attributevalue.filter((value) => {
              return (
                value.attribute.id.toString() === param &&
                params[param] === value.value.id.toString()
              );
            }).length > 0
          );
        });
      }

      if (storageItems.length > 0) {
        setFilterdStorage(storageItems);
      }
      calculateQuantity(storageItems);
      isAllAtributesSelected(storage);
    };

    filterProducts(params);
  }, [search, storage, navigate, initialComplete, status]);

  // const sortAttributes = (attributes: DetailAttribute[]) => {
  // 	// сортируем свойства товара, сначала required
  // 	let attr = [...attributes];
  // 	attr = attr.sort((x, y) => (x.required === y.required ? 0 : x.required ? -1 : 1));
  // 	return attr;
  // };

  useEffect(() => {
    // получаем свойства товара
    const getProduct = async (id: string) => {
      setStatus('loading');
      try {
        const { data } = await shopService.getProduct(id);
        addProduct(data);
        const attributes = [...data.attributes];

        if (attributes.length > 0) {
          attributes[0] = { ...attributes[0], required: true };
        }

        setAttributes(attributes);
        setStorage(data.storageitem);
        setMedia({
          image: data.promo_image,
          images: data.images,
          video: data.product_video,
          presentation: data.presentation,
        });
        setPrice({
          amount: data.product_price[0].amount,
          partner_amount: data.product_price[0].partner_amount,
        });
        setInfo({
          title: data.title,
          description: data.description,
          specifications: data.specifications,
        });

        setTables(data.product_group.tables);
      } catch (error) {}
      setStatus('loaded');
    };

    getProduct(id);
  }, [id]);

  return {
    status,
    filterdStorage,
    quantity,
    setQuantity,
    sendToCart,
    showSelectNote,
    setShowSelectNote,
    selectedQuantity,
    attributesSelected,
    attributes,
    storage,
    media,
    price,
    info,
    tables,
    productId,
  };
};

export default useProduct;
